<template>
  <div class="error-page">
    <div class="error-container">
      <v-icon
        class="error-icon"
        size="120"
        color="#9e9e9e"
      >
        mdi-bug-outline
      </v-icon>

      <h1 class="text-h5 error-title">
        {{ $t('error.title') }}
      </h1>

      <p class="text-subtitle-1 error-message">
        {{ $t('error.message') }}
      </p>

      <MBtn
        v-if="!isHomePage"
        to="/"
        variant="outlined"
        size="large"
        class="home-btn"
      >
        {{ $t('error.goHome') }}
      </MBtn>

      <!-- Development Mode Error Details -->
      <div
        v-if="isDevelopment && props.error"
        class="dev-error-details"
      >
        <v-divider class="my-4" />

        <v-card
          class="mt-3 error-details-card"
          flat
        >
          <v-card-text>
            <div class="error-detail-item">
              <strong>Status Code:</strong>
              <span class="error-code">{{ props.error.statusCode || 'Unknown' }}</span>
            </div>

            <div class="error-detail-item">
              <strong>Status Text:</strong>
              <span>{{ props.error.statusMessage || 'Unknown' }}</span>
            </div>

            <div
              v-if="props.error.url"
              class="error-detail-item"
            >
              <strong>URL:</strong>
              <span class="text-break">{{ props.error.url }}</span>
            </div>

            <div class="error-detail-item">
              <strong>Error Message:</strong>
              <span>{{ props.error.message || 'No message available' }}</span>
            </div>

            <div
              v-if="props.error.stack"
              class="error-detail-item"
            >
              <strong>Stack Trace:</strong>
              <pre class="stack-trace">{{ props.error.stack }}</pre>
            </div>

            <div
              v-if="props.error.data"
              class="error-detail-item"
            >
              <strong>Additional Data:</strong>
              <pre class="stack-trace">{{ JSON.stringify(props.error.data, null, 2) }}</pre>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface ErrorProps {
  error?: {
    statusCode?: number;
    statusMessage?: string;
    message?: string;
    stack?: string;
    url?: string;
    data?: any;
  };
}

const props = defineProps<ErrorProps>();

const route = useRoute();
const { t } = useI18n();
const { $development } = useNuxtApp();

const isHomePage = computed(() => route.path === '/');
const isDevelopment = computed(() => $development || import.meta.dev);

// Set page title
useHead({
  title: computed(() => `${t('error.pageTitle')}`),
  meta: [
    {
      name: 'robots',
      content: 'noindex, nofollow',
    },
  ],
});
</script>

<style scoped lang="scss">
.error-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fafbfc 0%, #f1f3f5 100%);
  padding: 2rem;
}

.error-container {
  max-width: 500px;
  width: 100%;
  text-align: center;
}

.error-icon {
  margin-bottom: 2rem;
}

.error-title {
  color: rgb(var(--v-theme-dark-900));
  margin-bottom: 1rem;
}

.error-message {
  color: rgb(var(--v-theme-dark-600));
  font-weight: 400;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.home-btn {
  margin-top: 1rem;
}

/* Development error details styling */
.dev-error-details {
  width: 100%;
  text-align: left;
}

.error-details-card {
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
}

.error-detail-item {
  margin-bottom: 1rem;

  strong {
    color: rgb(var(--v-theme-dark-900));
    margin-right: 0.5rem;
  }

  .error-code {
    color: rgb(var(--v-theme-error));
    font-weight: 600;
  }
}

.stack-trace {
  background-color: #f8f8f8;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 1rem;
  margin-top: 0.5rem;
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-all;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
}
</style>
